<template>
  <div>
    <div id="fluidpay-container" ref="fluidpayContainer"></div>
    <div class="form-group">
      <label for="fluidpay-first-name">First Name</label>
      <input
        id="fluidpay-first-name"
        v-model="localFirstName"
        type="text"
        class="form-control fluidpay-input"
        placeholder="First Name"
      />
    </div>
    <div class="form-group">
      <label for="fluidpay-last-name">Last Name</label>
      <input
        id="fluidpay-last-name"
        v-model="localLastName"
        type="text"
        class="form-control fluidpay-input"
        placeholder="Last Name"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'FluidPayForm',
  props: {
    apiKey: {
      type: String,
      required: false,
      default: ''
    },
    apiUrl: {
      type: String,
      required: false,
      default: ''
    },
    firstName: {
      type: String,
      default: ''
    },
    lastName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tokenizerWindow: null,
      isLoading: false,
      localFirstName: this.firstName || '',
      localLastName: this.lastName || ''
    };
  },
  mounted() {
    if (this.apiUrl) {
      this.initializeTokenizer();
    }
    // else: watcher below will trigger when apiUrl is populated
  },
  beforeUnmount() {
    if (this.tokenizerWindow) {
      this.tokenizerWindow = null;
    }
  },
  methods: {
    initializeTokenizer() {
      this.$emit('set-loading', true);

      if (typeof window.Tokenizer !== 'undefined') {
        this.setupFluidPayTokenizer();
        return;
      }

      if (!this.apiUrl || this.apiUrl.trim() === '') {
        this.$fnNotify({message: 'FluidPay API URL not configured. Please contact support.', type: 'error'});
        this.$emit('set-loading', false);
        return;
      }

      const scriptUrl = `${this.apiUrl.replace(/\/$/, '')}/tokenizer/tokenizer.js`;
      const existing = document.querySelector(`script[src="${scriptUrl}"]`);
      if (existing) {
        existing.remove();
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        this.setupFluidPayTokenizer();
      };
      script.onerror = () => {
        this.$fnNotify({message: 'Failed to load FluidPay tokenizer. Please refresh and try again.', type: 'error'});
        this.$emit('set-loading', false);
      };
      document.head.appendChild(script);
    },
    
    setupFluidPayTokenizer() {
      try {
        if (!this.apiKey || this.apiKey.trim() === '') {
          this.$fnNotify({message: 'FluidPay API key not configured. Please contact support.', type: 'error'});
          this.$emit('set-loading', false);
          return;
        }

        this.tokenizerWindow = new window.Tokenizer({
          url: this.apiUrl,
          apikey: this.apiKey,
          container: '#fluidpay-container',
          
          submission: (response) => {
            this.handleSubmission(response);
          },
          
          onLoad: () => {
            this.$emit('set-loading', false);
          },
          
          validCard: (card) => {
            
          },
          
          settings: {
            payment: {
              showTitle: true,
              types: ['card'], 
              card: {
                mask_number: false,
                requireCVV: true,
                strict_mode: false,
                
                user_required: {
                  first_name: false,
                  last_name: false,
                  email: false,
                  phone: false
                },
                
                billing_required: {
                  address: false,
                  city: false,
                  state: false,
                  zip: false,
                  country: false
                }
              }
            },
            
            user: {
              showInline: false,
              showName: false,
              showEmail: false,
              showPhone: false
            },
            
            shipping: {
              show: false
            },
            billing: {
              show: false
            },
            
            styles: {
              body: {
                'font-family': '"Open Sans", sans-serif'
              },
              input: {
                'min-height': '40px',
                'border': '1px solid #ced4da',
                'border-radius': '4px',
                'padding': '8px 12px',
                'background': '#fff',
                'font-size': '16px',
                'box-sizing': 'border-box',
                'color': '#212529',
                'line-height': '1.5',
                'font-weight': '400'
              },
              '.payment input': {
                'background-color': '#fff'
              },
              'input:focus': {
                'outline': '3px solid rgba(0, 123, 255, 0.16)',
                'outline-offset': '2px',
                'border-color': '#80bdff'
              }
            }
          }
        });
      } catch (error) {
        const errorMsg = (error && error.message) ? error.message : 'Tokenizer initialization failed';
        this.$fnNotify({message: 'Failed to initialize payment form. Please try again.', type: 'error'});
        this.$emit('set-loading', false);
        console.error('FluidPayForm initialization error:', errorMsg);
      }
    },
    
    handleSubmission(response) {
      
      switch(response.status) {
        case 'success':
            
          if (response.token) {
            try {
              // Create FluidPay customer via backend service using the token
              this.$fnApi.call('/api/v1/cards/fluidpay/customer', 'POST', {
                token: response.token,
                first_name: this.localFirstName,
                last_name: this.localLastName,
                description: `${this.localFirstName} ${this.localLastName}`
              }).then(resp => {

                const customer = resp && resp.data ? resp.data : null;
                if (!customer || customer.status !== 'success') {
                  const errorMsg = (customer && customer.msg) ? customer.msg : 'Create customer failed';
                  throw new Error(errorMsg);
                }

                const defaultCard = this.getDefaultCardDetails(customer);
                const customerId = customer.data && customer.data.id ? customer.data.id : null;

                let last4 = '';
                let bin = '';
                let cardType = '';
                let expirationDateMMYY = '';

                if (defaultCard) {
                  const masked = defaultCard.masked_number || '';
                  const parsedMasked = this.parseMaskedNumber(masked);
                  last4 = parsedMasked.last4 || '';
                  bin = parsedMasked.bin || '';
                  const parsedExp = this.parseExpiration(defaultCard.expiration_date);
                  const mm = parsedExp.month ? String(parsedExp.month).padStart(2, '0') : '';
                  const yy = parsedExp.year ? String(parsedExp.year).slice(-2) : '';
                  expirationDateMMYY = mm && yy ? `${mm}${yy}` : '';
                  cardType = customer.card_type || '';
                }

                // Build payload compatible with parent onVerify flow
                const tokenizedCard = {
                  cardId: customerId,
                  cardLastFour: last4,
                  expirationDate: expirationDateMMYY,
                  cvv: null,
                  fluid_pay_customer_id: customerId,
                  firstName: this.localFirstName,
                  lastName: this.localLastName
                };

                const verifyData = {
                  successful: true,
                  bin: bin,
                  network: cardType || 'unknown',
                  provider: 'fluidpay',
                  verifyType: customer.card_type || '',
                  country: 'US'
                };

                this.$emit('verified', { tokenizedCard, verifyData });

              }).catch(err => {
                // Defensive error handling - handle multiple error response structures
                let errorMsg = 'Failed to create FluidPay customer.';
                if (err) {
                  if (typeof err === 'string') {
                    errorMsg = err;
                  } else if (err.message) {
                    errorMsg = err.message;
                  } else if (err.error && typeof err.error === 'string') {
                    errorMsg = err.error;
                  } else if (err.data && err.data.message) {
                    errorMsg = err.data.message;
                  }
                }
                this.$fnNotify({message: errorMsg, type: 'error'});
                this.$emit('token-error', { message: errorMsg });
              });
              
            } catch (e) {
              // Defensive error handling in try-catch
              let errorMsg = 'Failed to create FluidPay customer.';
              if (e) {
                if (typeof e === 'string') {
                  errorMsg = e;
                } else if (e.message) {
                  errorMsg = e.message;
                }
              }
              this.$fnNotify({message: errorMsg, type: 'error'});
              this.$emit('token-error', { message: errorMsg });
            }
          } else {
            this.$fnNotify({message: 'Token creation failed. Please try again.', type: 'error'});
            this.$emit('token-error', { message: 'No token returned' });
          }
          break;
          
        case 'error':
            
          const errorMsg = response.msg || 'An error occurred while processing your card.';
          this.$fnNotify({message: errorMsg, type: 'error'});
          this.$emit('token-error', { message: errorMsg });
          break;
          
        case 'validation':
            
          const validationMsg = this.formatValidationErrors(response.invalid);
          this.$fnNotify({message: validationMsg, type: 'error'});
          this.$emit('token-error', { message: validationMsg });
          break;
          
        default:
          this.$fnNotify({message: 'Unexpected response from payment processor.', type: 'error'});
          this.$emit('token-error', { message: 'Unexpected response from payment processor.' });
      }
    },
    // Deprecated in this flow: Fetching customer on frontend is no longer used.
    getDefaultCardDetails(customerResponse) {
      try {
        var customer = null;
        if (customerResponse && customerResponse.data && customerResponse.data.data && customerResponse.data.data.customer) {
          customer = customerResponse.data.data.customer;
        }
        if (!customer) return null;

        var defaults = customer.defaults || {};
        var pmType = (defaults.payment_method_type || '').toLowerCase();
        var pmId = defaults.payment_method_id;
        if (pmType !== 'card' || !pmId) return null;

        var cards = (customer.payments && customer.payments.cards) ? customer.payments.cards : [];
        var match = null;
        for (var i = 0; i < cards.length; i++) {
          if (cards[i] && cards[i].id === pmId) {
            match = cards[i];
            break;
          }
        }
        return match;
      } catch (e) {
        return null;
      }
    },
    parseMaskedNumber(masked) {
      if (!masked || typeof masked !== 'string') return { bin: '', last4: '' };
      // Common format: 411111******1111 or 411111********1111
      const digits = masked.replace(/[^0-9]/g, '');
      const last4 = digits.slice(-4);
      const bin = digits.slice(0, 6);
      return { bin, last4 };
    },
    parseExpiration(exp) {
      if (!exp) return { month: null, year: null };
      // Handle MM/YY or MM/YYYY
      if (typeof exp === 'string') {
        const mmYyMatch = exp.match(/^(\d{2})\/(\d{2,4})$/);
        if (mmYyMatch) {
          const month = parseInt(mmYyMatch[1], 10);
          let year = parseInt(mmYyMatch[2], 10);
          if (mmYyMatch[2].length === 2) {
            // Assume 2000+ for 2-digit years
            year += 2000;
          }
          return { month, year };
        }
      }
      // Fallback: try ISO date string
      try {
        const d = new Date(exp);
        if (isNaN(d.getTime())) return { month: null, year: null };
        return { month: (d.getUTCMonth() + 1), year: d.getUTCFullYear() };
      } catch (_) {
        return { month: null, year: null };
      }
    },
    
    formatValidationErrors(errors) {
      if (!errors || Object.keys(errors).length === 0) {
        return 'Please check your card details and try again.';
      }
      
      const errorMessages = Object.entries(errors)
        .map(([field, error]) => `${field}: ${error}`)
        .join(', ');
      
      return `Validation errors: ${errorMessages}`;
    },
    
    extractLast4(response) {
        
      return response.card.last4 
        || response.payment_method.card.last4 
        || response.last4 
        || '';
    },
    
    extractCardType(response) {
        
      return response.card.card_type 
        || response.card.type 
        || response.payment_method.card.card_type 
        || response.card_type 
        || '';
    },
    
    extractExpirationMonth(response) {
      return response.card.expiration_month 
        || response.payment_method.card.expiration_month 
        || null;
    },
    
    extractExpirationYear(response) {
      return response.card.expiration_year 
        || response.payment_method.card.expiration_year 
        || null;
    },
    
    extractBin(response) {
      return response.card.bin 
        || response.payment_method.card.bin 
        || response.bin 
        || '';
    },
    
    submitCard() {
        
      if (!this.localFirstName || !this.localFirstName.trim()) {
        this.$fnNotify({message: 'First name is required', type: 'error'});
        return;
      }
      
      if (!this.localLastName || !this.localLastName.trim()) {
        this.$fnNotify({message: 'Last name is required', type: 'error'});
        return;
      }
      
      this.$emit('set-loading', true);
      
      if (this.tokenizerWindow) {
        try {
            this.tokenizerWindow.submit();
        } catch (error) {
          this.$fnNotify({message: 'Failed to submit payment form. Please try again.', type: 'error'});
          this.$emit('set-loading', false);
          const errorMsg = (error && error.message) ? error.message : 'Submit failed';
          this.$emit('token-error', { message: errorMsg });
        }
      } else {
        this.$fnNotify({message: 'Payment form not initialized. Please refresh and try again.', type: 'error'});
        this.$emit('set-loading', false);
      }
    }
  }
  ,
  watch: {
    firstName: {
      handler(newVal) {
        this.localFirstName = newVal || '';
      },
      immediate: true
    },
    lastName: {
      handler(newVal) {
        this.localLastName = newVal || '';
      },
      immediate: true
    },
    apiUrl(newVal) {
      if (newVal && !this.tokenizerWindow) {
        this.initializeTokenizer();
      }
    },
  }
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.fluidpay-input {
  min-height: 40px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
  background: #fff;
  font-size: 16px;
  box-sizing: border-box;
  color: #212529;
  line-height: 1.5;
  font-weight: 400;
  width: 100%;
  font-family: "Open Sans", sans-serif;
}

.fluidpay-input:focus {
  outline: 3px solid rgba(0, 123, 255, 0.16);
  outline-offset: 2px;
  border-color: #80bdff;
}

#fluidpay-container {
  margin-bottom: 15px;
}
</style>
